"use client";

import React, { useState, useCallback, useEffect } from "react";
import Image from "next/image";
import { MdClose } from "react-icons/md";
import { IoCompassOutline } from "react-icons/io5"; // Added for Orientation
import { useTranslations } from "next-intl";
import styles from "./listing-amenities.module.css";
import { Facilities, View, Orientation } from "@/types/types";
import { facilityRules } from "@/lib/getFacilities/getAvailableFacilities";

interface ListingFacilitiesProps {
  facilities: Facilities;
  view?: View;
  orientation?: Orientation;
}

// 🚀 THE FIX: A unified type so TypeScript knows both icon strategies are valid
type FeatureItem = {
  id: string;
  label: string;
  iconUrl?: string;
  iconNode?: React.ReactNode;
};

export default function ListingFacilities({
  facilities,
  view,
  orientation,
}: ListingFacilitiesProps) {
  // Adjust your translation namespace if needed (e.g., just "ListingView" or something else)
  const t = useTranslations("ListingView.Facilities");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 1. Process Views
  const activeViews = view
    ? (Object.keys(view) as (keyof View)[]).filter((key) => view[key])
    : [];

  const viewItems: FeatureItem[] = activeViews.map((v) => ({
    id: v,
    label: t(`view.${v}`, { default: v }),
    iconUrl: `https://d1ktxmigjvpff7.cloudfront.net/view-icons/${v}.png`,
  }));

  // 2. Process Orientation
  const activeOrientations = orientation
    ? (Object.keys(orientation) as (keyof Orientation)[]).filter(
        (key) => orientation[key],
      )
    : [];

  const orientationItems: FeatureItem[] = activeOrientations.map((o) => ({
    id: o,
    label: t(`orientation.${o}`, { default: o }),
    iconNode: <IoCompassOutline className={styles.reactIcon} />,
  }));

  // 3. Process Facilities
  const activeFacilities = facilities
    ? (Object.keys(facilities) as (keyof Facilities)[]).filter(
        (key) => facilities[key],
      )
    : [];

  const facilityItems: FeatureItem[] = activeFacilities
    .map((f) => {
      const rule = facilityRules[f];
      if (!rule) return null;
      return {
        id: f,
        label: t(rule.label), // 🚀 Using the exact logic you specified
        iconUrl: rule.icon_url,
      };
    })
    .filter(Boolean) as FeatureItem[];

  // 4. Combine Everything (Views & Orientations first!)
  const combinedFeatures: FeatureItem[] = [
    ...viewItems,
    ...orientationItems,
    ...facilityItems,
  ];

  const DISPLAY_LIMIT = 8;
  const shouldTruncate = combinedFeatures.length > DISPLAY_LIMIT;
  const visibleFeatures = combinedFeatures.slice(0, DISPLAY_LIMIT);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (isModalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  if (combinedFeatures.length === 0) return null;

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>
        {t("facilitiesTitle", { default: "Facilități & Vedere" })}
      </h2>

      <ul className={styles.grid}>
        {visibleFeatures.map((feature, index) => (
          <li key={index} className={styles.facilityItem}>
            {/* Because of our unified FeatureItem type, TS allows this check */}
            {feature.iconUrl ? (
              <Image
                className={styles.icon}
                src={feature.iconUrl}
                width={32}
                height={32}
                alt={feature.label}
                unoptimized
              />
            ) : (
              feature.iconNode
            )}
            <span className={styles.facilityText}>{feature.label}</span>
          </li>
        ))}
      </ul>

      {shouldTruncate && (
        <button
          className={styles.showAllBtn}
          onClick={() => setIsModalOpen(true)}
        >
          {t("showAllFacilities", {
            count: combinedFeatures.length,
            default: `Arată toate cele ${combinedFeatures.length} facilități`,
          })}
        </button>
      )}

      {/* V2 MODAL */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>{t("facilitiesTitle", { default: "Toate facilitățile" })}</h3>
              <button className={styles.closeBtn} onClick={closeModal}>
                <MdClose />
              </button>
            </div>
            <div className={styles.modalScrollArea}>
              <ul className={styles.modalList}>
                {combinedFeatures.map((feature, index) => (
                  <li key={index} className={styles.modalListItem}>
                    {feature.iconUrl ? (
                      <Image
                        className={styles.icon}
                        src={feature.iconUrl}
                        width={32}
                        height={32}
                        alt={feature.label}
                        unoptimized
                      />
                    ) : (
                      feature.iconNode
                    )}
                    <span className={styles.facilityText}>{feature.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
